<template>
  <div class="i-quiz-question">
    <div class="number">{{ questionNumber }}</div>
    <div class="title">{{ title }}</div>
    <div class="options">
      <QuizOption
        v-for="opt in options"
        :key="opt.answer"
        :text="opt.label"
        :selected="selected === opt.answer"
        @select="onSelect(opt)"
      />
    </div>
    <QuizCallout
      v-if="calloutVisible"
      :html="calloutHtml"
      :visible="calloutVisible"
    />
    <button
      v-if="continueVisible"
      class="continue"
      @click="$emit('continue')"
    >
      Continue
    </button>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import QuizOption from "./QuizOption.vue"
import QuizCallout from "./QuizCallout.vue"

const props = defineProps({
  questionNumber: [String, Number],
  title: String,
  options: Array,
  calloutTriggers: { type: Array, default: () => [] },
  calloutTexts: { type: Object, default: () => ({}) },
  defaultCalloutHtml: { type: String, default: "" },
  situation: { type: String, default: "" }
})

const emit = defineEmits(["answer", "continue"])

const selected = ref(null)
const calloutVisible = ref(false)
const continueVisible = ref(false)

const calloutHtml = computed(() => {
  if (props.situation && props.calloutTexts[props.situation]) {
    return props.calloutTexts[props.situation]
  }
  return props.defaultCalloutHtml
})

function onSelect(opt) {
  selected.value = opt.answer
  emit("answer", opt.answer)

  if (props.calloutTriggers.includes(opt.answer)) {
    calloutVisible.value = true
    continueVisible.value = true
  } else {
    calloutVisible.value = false
    continueVisible.value = false
  }
}

// Reset when question changes
watch(
  () => props.title,
  () => {
    selected.value = null
    calloutVisible.value = false
    continueVisible.value = false
  }
)

defineExpose({ selected })
</script>

<style lang="scss" scoped>
.i-quiz-question {
  .number {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: $navy;
    margin-bottom: 10px;

    @include respond-to($breakpoint-md) {
      margin-bottom: 12px;
    }
  }

  .title {
    font-family: $serif;
    font-size: clamp(18px, 3vw, 24px);
    font-weight: 400;
    line-height: 1.3;
    letter-spacing: -0.02em;
    color: $ink;
    margin-bottom: 20px;

    @include respond-to($breakpoint-md) {
      font-size: clamp(20px, 3vw, 30px);
      margin-bottom: 26px;
    }
  }

  .options {
    display: flex;
    flex-direction: column;
    gap: 10px;

    @include respond-to($breakpoint-md) {
      gap: 12px;
    }
  }

  .continue {
    margin-top: 14px;
    background: $amber-mid;
    color: $white;
    font-family: $sans;
    font-size: 15px;
    font-weight: 600;
    padding: 14px 36px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: opacity 0.2s;
    width: 100%;
    display: block;

    @include respond-to($breakpoint-md) {
      margin-top: 16px;
    }

    &:hover {
      opacity: 0.88;
    }
  }
}
</style>
